import React from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import cnames from "classnames";
import { compose } from "redux";

import Button from "@redbubble/design-system/react/Button";
import Box from "@redbubble/design-system/react/Box";
import Image from "@redbubble/design-system/react/Image";
import * as constants from "@redbubble/design-system/react/constants";

import { useAnalyticsActions } from "../../containers/redux/withAnalytics";
import { withListEntityInclusionsFromCache } from "../../containers/apollo/withListEntityInclusions";
import { analyticsPayload } from "../../lib/analytics";
import FavoriteIcon, { SMALL, MEDIUM, LARGE } from "../FavoriteIcon";
import { itemShape, listManagerShape } from "./";
import styles from "./styles.scss";

const Heart = (props) => {
  const {
    listManager,
    isLoggedIn,
    size,
    listEntityInclusions,
    item,
    locale,
    openLoginSignupModal,
    noDropShadow,
    strong,
    fluid,
    onClick,
  } = props;
  const { logEvent } = useAnalyticsActions();

  if (
    !listManager ||
    !item ||
    !locale ||
    (!isLoggedIn && !openLoginSignupModal)
  ) {
    return null;
  }

  const handleAnonClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const fullTitle = `${item.workTitle} ${item.inventoryItemDescription}`;

    const renderImage = () => (
      <Box paddingBottom="m" display="flex" alignItems="center">
        <Image
          src={item.preview}
          alt={fullTitle}
          className={styles.productImage}
        />
      </Box>
    );
    openLoginSignupModal({
      header: get(item, "preview") ? renderImage() : null,
    });
  };

  const handleClick = async (e) => {
    e.preventDefault();

    const { addItemToDefaultList, openListPicker, openListNotification } =
      listManager;

    if (listEntityInclusions.length !== 0) {
      openListPicker(item);
      return;
    }

    const { entityId, entityType } = item;
    const { data } = await addItemToDefaultList({
      item: { entityId, entityType },
      locale,
    });

    const listName = get(data, "addItemToDefaultList.defaultList.name");
    const listId = get(data, "addItemToDefaultList.defaultList.listId");
    const inclusions = get(data, "addItemToDefaultList.inclusions");

    logEvent({
      analytics: analyticsPayload.addItemToDefaultList(listName),
      dataLayer: { event: "addItemToDefaultList", listName, item },
    });
    if (listName && inclusions) {
      openListNotification({ item, listName, listId });
    }
    onClick();
  };

  const fullStoryTag = "anon-heart-button";
  const className = cnames("js-heartButton", {
    [fullStoryTag]: !isLoggedIn,
  });

  const isAddedToSomeList = !!(
    listEntityInclusions && listEntityInclusions.length > 0
  );

  return (
    <Box className={className}>
      <Button
        data-testid="favorite-button"
        elevation={noDropShadow ? null : constants.ELEVATION_MEDIUM}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={isLoggedIn ? handleClick : handleAnonClick}
        circle
        strong={strong}
        size={size}
        iconAfter={<FavoriteIcon size={size} favorited={isAddedToSomeList} />}
        fluid={fluid}
        aria-label="Add to favorites"
      />
    </Box>
  );
};

Heart.propTypes = {
  isLoggedIn: PropTypes.bool,
  item: itemShape,
  listEntityInclusions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      listId: PropTypes.string,
    })
  ),
  listManager: listManagerShape,
  locale: PropTypes.string,
  noDropShadow: PropTypes.bool,
  openLoginSignupModal: PropTypes.func,
  size: PropTypes.oneOf([SMALL, MEDIUM, LARGE]),
  strong: PropTypes.bool,
  fluid: PropTypes.bool,
  onClick: PropTypes.func,
};

Heart.defaultProps = {
  isLoggedIn: false,
  item: null,
  listEntityInclusions: [],
  listManager: null,
  locale: "en",
  noDropShadow: false,
  openLoginSignupModal: null,
  size: SMALL,
  strong: false,
  fluid: false,
  onClick: () => null,
};

export default compose(withListEntityInclusionsFromCache)(Heart);
export { Heart as HeartButton };
